/**
 * Языки, которые распознаёт наш ASR-провайдер (ElevenLabs Scribe v2).
 *
 * Источник — текстовая документация провайдера, а НЕ его API: эндпоинта,
 * отдающего список языков, у ElevenLabs нет (параметр `language_code`
 * документирован как ISO-639-1 либо ISO-639-3, значение по умолчанию `null` =
 * автоопределение). Поэтому справочник живёт в коде, рядом с
 * provider-catalog.ts, который уже играет ту же роль для вендоров и моделей.
 *
 * Тир — уровень точности по WER из той же документации. Он показывается в UI
 * рядом с языком: пользователю, выбирающему язык распознавания, это самое
 * полезное, что можно сообщить, и живой список из API его бы не дал.
 *
 * Набор специфичен для Scribe v2. Второй набор появится вместе с реальным
 * вторым ASR-провайдером. Deepgram им так и не стал — 2 транскрипта из 104
 * тысяч на проде — и убран из вендоров 2026-09-11.
 *
 * Источник: https://elevenlabs.io/docs/overview/capabilities/speech-to-text
 */

export type AsrAccuracyTier = 'excellent' | 'high' | 'good' | 'moderate';

export interface AsrLanguage {
  /** ISO-639-1, если у языка он есть. Хранимое и отправляемое значение. */
  iso1?: string;
  /** ISO-639-3 — форма, в которой язык перечислен у провайдера. */
  iso3: string;
  /** Английское название. Фолбэк, когда Intl.DisplayNames не знает кода. */
  name: string;
  tier: AsrAccuracyTier;
}

export const ASR_LANGUAGES: readonly AsrLanguage[] = [
  // Excellent (≤5% WER)
  { iso1: 'be', iso3: 'bel', name: 'Belarusian', tier: 'excellent' },
  { iso1: 'bs', iso3: 'bos', name: 'Bosnian', tier: 'excellent' },
  { iso1: 'bg', iso3: 'bul', name: 'Bulgarian', tier: 'excellent' },
  { iso1: 'ca', iso3: 'cat', name: 'Catalan', tier: 'excellent' },
  { iso1: 'hr', iso3: 'hrv', name: 'Croatian', tier: 'excellent' },
  { iso1: 'cs', iso3: 'ces', name: 'Czech', tier: 'excellent' },
  { iso1: 'da', iso3: 'dan', name: 'Danish', tier: 'excellent' },
  { iso1: 'nl', iso3: 'nld', name: 'Dutch', tier: 'excellent' },
  { iso1: 'en', iso3: 'eng', name: 'English', tier: 'excellent' },
  { iso1: 'et', iso3: 'est', name: 'Estonian', tier: 'excellent' },
  { iso1: 'fi', iso3: 'fin', name: 'Finnish', tier: 'excellent' },
  { iso1: 'fr', iso3: 'fra', name: 'French', tier: 'excellent' },
  { iso1: 'gl', iso3: 'glg', name: 'Galician', tier: 'excellent' },
  { iso1: 'de', iso3: 'deu', name: 'German', tier: 'excellent' },
  { iso1: 'el', iso3: 'ell', name: 'Greek', tier: 'excellent' },
  { iso1: 'hu', iso3: 'hun', name: 'Hungarian', tier: 'excellent' },
  { iso1: 'is', iso3: 'isl', name: 'Icelandic', tier: 'excellent' },
  { iso1: 'id', iso3: 'ind', name: 'Indonesian', tier: 'excellent' },
  { iso1: 'it', iso3: 'ita', name: 'Italian', tier: 'excellent' },
  { iso1: 'ja', iso3: 'jpn', name: 'Japanese', tier: 'excellent' },
  { iso1: 'kn', iso3: 'kan', name: 'Kannada', tier: 'excellent' },
  { iso1: 'lv', iso3: 'lav', name: 'Latvian', tier: 'excellent' },
  { iso1: 'mk', iso3: 'mkd', name: 'Macedonian', tier: 'excellent' },
  { iso1: 'ms', iso3: 'msa', name: 'Malay', tier: 'excellent' },
  { iso1: 'ml', iso3: 'mal', name: 'Malayalam', tier: 'excellent' },
  { iso1: 'no', iso3: 'nor', name: 'Norwegian', tier: 'excellent' },
  { iso1: 'pl', iso3: 'pol', name: 'Polish', tier: 'excellent' },
  { iso1: 'pt', iso3: 'por', name: 'Portuguese', tier: 'excellent' },
  { iso1: 'ro', iso3: 'ron', name: 'Romanian', tier: 'excellent' },
  { iso1: 'ru', iso3: 'rus', name: 'Russian', tier: 'excellent' },
  { iso1: 'sk', iso3: 'slk', name: 'Slovak', tier: 'excellent' },
  { iso1: 'es', iso3: 'spa', name: 'Spanish', tier: 'excellent' },
  { iso1: 'sv', iso3: 'swe', name: 'Swedish', tier: 'excellent' },
  { iso1: 'tr', iso3: 'tur', name: 'Turkish', tier: 'excellent' },
  { iso1: 'uk', iso3: 'ukr', name: 'Ukrainian', tier: 'excellent' },
  { iso1: 'vi', iso3: 'vie', name: 'Vietnamese', tier: 'excellent' },

  // High accuracy (>5–10% WER)
  { iso1: 'hy', iso3: 'hye', name: 'Armenian', tier: 'high' },
  { iso1: 'az', iso3: 'aze', name: 'Azerbaijani', tier: 'high' },
  { iso1: 'bn', iso3: 'ben', name: 'Bengali', tier: 'high' },
  { iso3: 'yue', name: 'Cantonese', tier: 'high' },
  { iso3: 'fil', name: 'Filipino', tier: 'high' },
  { iso1: 'ka', iso3: 'kat', name: 'Georgian', tier: 'high' },
  { iso1: 'gu', iso3: 'guj', name: 'Gujarati', tier: 'high' },
  { iso1: 'hi', iso3: 'hin', name: 'Hindi', tier: 'high' },
  { iso1: 'kk', iso3: 'kaz', name: 'Kazakh', tier: 'high' },
  { iso1: 'lt', iso3: 'lit', name: 'Lithuanian', tier: 'high' },
  { iso1: 'mt', iso3: 'mlt', name: 'Maltese', tier: 'high' },
  { iso3: 'cmn', name: 'Mandarin', tier: 'high' },
  { iso1: 'mr', iso3: 'mar', name: 'Marathi', tier: 'high' },
  { iso1: 'ne', iso3: 'nep', name: 'Nepali', tier: 'high' },
  { iso1: 'or', iso3: 'ori', name: 'Odia', tier: 'high' },
  { iso1: 'fa', iso3: 'fas', name: 'Persian', tier: 'high' },
  { iso1: 'sr', iso3: 'srp', name: 'Serbian', tier: 'high' },
  { iso1: 'sl', iso3: 'slv', name: 'Slovenian', tier: 'high' },
  { iso1: 'sw', iso3: 'swa', name: 'Swahili', tier: 'high' },
  { iso1: 'ta', iso3: 'tam', name: 'Tamil', tier: 'high' },
  { iso1: 'te', iso3: 'tel', name: 'Telugu', tier: 'high' },

  // Good (>10–20% WER)
  { iso1: 'af', iso3: 'afr', name: 'Afrikaans', tier: 'good' },
  { iso1: 'ar', iso3: 'ara', name: 'Arabic', tier: 'good' },
  { iso1: 'as', iso3: 'asm', name: 'Assamese', tier: 'good' },
  { iso3: 'ast', name: 'Asturian', tier: 'good' },
  { iso1: 'my', iso3: 'mya', name: 'Burmese', tier: 'good' },
  { iso1: 'ha', iso3: 'hau', name: 'Hausa', tier: 'good' },
  { iso1: 'he', iso3: 'heb', name: 'Hebrew', tier: 'good' },
  { iso1: 'jv', iso3: 'jav', name: 'Javanese', tier: 'good' },
  { iso1: 'ko', iso3: 'kor', name: 'Korean', tier: 'good' },
  { iso1: 'ky', iso3: 'kir', name: 'Kyrgyz', tier: 'good' },
  { iso1: 'lb', iso3: 'ltz', name: 'Luxembourgish', tier: 'good' },
  { iso1: 'mi', iso3: 'mri', name: 'Māori', tier: 'good' },
  { iso1: 'oc', iso3: 'oci', name: 'Occitan', tier: 'good' },
  { iso1: 'pa', iso3: 'pan', name: 'Punjabi', tier: 'good' },
  { iso1: 'tg', iso3: 'tgk', name: 'Tajik', tier: 'good' },
  { iso1: 'th', iso3: 'tha', name: 'Thai', tier: 'good' },
  { iso1: 'uz', iso3: 'uzb', name: 'Uzbek', tier: 'good' },
  { iso1: 'cy', iso3: 'cym', name: 'Welsh', tier: 'good' },

  // Moderate (>25–50% WER)
  { iso1: 'am', iso3: 'amh', name: 'Amharic', tier: 'moderate' },
  { iso1: 'lg', iso3: 'lug', name: 'Ganda', tier: 'moderate' },
  { iso1: 'ig', iso3: 'ibo', name: 'Igbo', tier: 'moderate' },
  { iso1: 'ga', iso3: 'gle', name: 'Irish', tier: 'moderate' },
  { iso1: 'km', iso3: 'khm', name: 'Khmer', tier: 'moderate' },
  { iso1: 'ku', iso3: 'kur', name: 'Kurdish', tier: 'moderate' },
  { iso1: 'lo', iso3: 'lao', name: 'Lao', tier: 'moderate' },
  { iso1: 'mn', iso3: 'mon', name: 'Mongolian', tier: 'moderate' },
  { iso3: 'nso', name: 'Northern Sotho', tier: 'moderate' },
  { iso1: 'ps', iso3: 'pus', name: 'Pashto', tier: 'moderate' },
  { iso1: 'sn', iso3: 'sna', name: 'Shona', tier: 'moderate' },
  { iso1: 'sd', iso3: 'snd', name: 'Sindhi', tier: 'moderate' },
  { iso1: 'so', iso3: 'som', name: 'Somali', tier: 'moderate' },
  { iso1: 'ur', iso3: 'urd', name: 'Urdu', tier: 'moderate' },
  { iso1: 'wo', iso3: 'wol', name: 'Wolof', tier: 'moderate' },
  { iso1: 'xh', iso3: 'xho', name: 'Xhosa', tier: 'moderate' },
  { iso1: 'yo', iso3: 'yor', name: 'Yoruba', tier: 'moderate' },
  { iso1: 'zu', iso3: 'zul', name: 'Zulu', tier: 'moderate' },
];

/** Значение, которое кладётся в настройки и уходит провайдеру. */
export function asrLanguageStoredCode(lang: AsrLanguage): string {
  return lang.iso1 ?? lang.iso3;
}

const ASR_LANGUAGE_CODES: ReadonlySet<string> = new Set(
  ASR_LANGUAGES.flatMap((l) => (l.iso1 ? [l.iso1, l.iso3] : [l.iso3]))
);

/** `'auto'` либо любой код каталога в форме ISO-639-1 или ISO-639-3. */
export function isSupportedAsrLanguage(code: string): boolean {
  return code === 'auto' || ASR_LANGUAGE_CODES.has(code);
}
